import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';

/** A passing note at the bottom of the page. `error` reads as one: a cross, red, announced. */
export type ToastOptions = { kind?: 'success' | 'error' };
type ToastCtx = { toast: (message: string, options?: ToastOptions) => void };
const Ctx = createContext<ToastCtx>({ toast: () => undefined });

export function ToastProvider({ children }: { children: ReactNode }) {
  const [entry, setEntry] = useState<{
    id: number;
    message: string;
    kind: 'success' | 'error';
  } | null>(null);
  const timer = useRef<number | undefined>(undefined);
  const toast = useCallback((message: string, options?: ToastOptions) => {
    window.clearTimeout(timer.current);
    const kind = options?.kind ?? 'success';
    setEntry({ id: Date.now(), message, kind });
    timer.current = window.setTimeout(() => setEntry(null), kind === 'error' ? 6000 : 3400);
  }, []);
  const value = useMemo(() => ({ toast }), [toast]);
  return (
    <Ctx.Provider value={value}>
      {children}
      {entry && (
        <div
          key={entry.id}
          className={`toast${entry.kind === 'error' ? ' error' : ''}`}
          role={entry.kind === 'error' ? 'alert' : 'status'}
        >
          <span
            className="check"
            aria-hidden="true"
            style={{ width: 18, height: 18, background: '#fff', color: '#111', fontSize: 10 }}
          >
            {entry.kind === 'error' ? '×' : '✓'}
          </span>
          {entry.message}
        </div>
      )}
    </Ctx.Provider>
  );
}

export function useToast() {
  return useContext(Ctx).toast;
}
